import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
  MenuItem,
} from "@mui/material";
import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";

const CustomerAdd = ({ open, onClose, token }) => {
  const { t } = useTranslation();
  const { handleAdd, loading, resetForm } = useAdd("shop/customers", token);
  const { data: shopsData, loading: shopLoading } = useFetchObjects(
    ["shop"],
    "shop/shop",
    token
  );
  const { data: currenciesData, loading: currencyLoading } = useFetchObjects(
    ["currency"],
    "shop/currencies",
    token
  );

  const shops = shopsData?.results || [];
  const currencies = currenciesData?.results || [];

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [contact, setContact] = useState("");
  const [address, setAddress] = useState("");
  const [selectedShop, setSelectedShop] = useState("");
  const [openingBalance, setOpeningBalance] = useState("");
  const [openingBalanceCurrency, setOpeningBalanceCurrency] = useState("");

  const handleSubmit = () => {
    const formData = new FormData();
    formData.append("name", name.trim());
    formData.append("email", email.trim());
    formData.append("contact", contact.trim());
    formData.append("address", address.trim());
    formData.append("shop", selectedShop);
    formData.append(
      "opening_balance",
      openingBalance === "" ? "0" : openingBalance
    );
    if (openingBalanceCurrency)
      formData.append("opening_balance_currency", openingBalanceCurrency);
    handleAdd(formData);
  };

  useEffect(() => {
    if (resetForm) {
      setName("");
      setEmail("");
      setContact("");
      setAddress("");
      setSelectedShop("");
      setOpeningBalance("");
      setOpeningBalanceCurrency("");
      onClose();
    }
  }, [resetForm, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("customer.addCustomer")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label={t("customer.customerName")}
            type="text"
            fullWidth
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("customer.customerEmail")}
            type="email"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("customer.customerContact")}
            type="text"
            fullWidth
            value={contact}
            onChange={(e) => setContact(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("customer.customerAddress")}
            type="text"
            fullWidth
            multiline
            rows={3}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t(
              "customer.openingBalance",
              "Opening balance (existing dues)"
            )}
            type="number"
            fullWidth
            value={openingBalance}
            onChange={(e) => setOpeningBalance(e.target.value)}
            inputProps={{ min: 0, step: "0.01" }}
          />
          <TextField
            select
            margin="dense"
            label={t(
              "customer.openingBalanceCurrency",
              "Opening balance currency"
            )}
            fullWidth
            value={openingBalanceCurrency}
            onChange={(e) => setOpeningBalanceCurrency(e.target.value)}
            disabled={currencyLoading}
          >
            <MenuItem value="">{t("customer.none", "None")}</MenuItem>
            {(currencies || []).map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                {c.title}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            margin="dense"
            label={t("customer.selectShop")}
            fullWidth
            required
            value={selectedShop}
            onChange={(e) => setSelectedShop(e.target.value)}
          >
            {shopLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              (shops || []).map((shop) => (
                <MenuItem key={shop.id} value={String(shop.id)}>
                  {shop.name}
                </MenuItem>
              ))
            )}
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("customer.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("customer.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerAdd;
